import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingBag, PlayCircle, User, ArrowRight, Sparkles, BookMarked } from "lucide-react";
import api from "@/lib/api";
import { CourseThumbnail } from "@/components/CourseThumbnail";
import { StarRating } from "@/components/StarRating";

export default function CourseMarketplacePage() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/courses/marketplace").then((res) => setCourses(res.data)).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="courses-marketplace-loading">
        <div className="w-10 h-10 border-2 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="courses-marketplace-page">
      <div className="flex items-center gap-3 mb-2">
        <ShoppingBag className="w-6 h-6 text-pink-400" />
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-pink-400">Creator Courses</span>
      </div>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-white">Browse Courses</h1>
      <p className="text-sm sm:text-base text-slate-400 mt-2 max-w-2xl">
        Video courses from real creators — every video comes with AI-generated notes (Key Points, Summary, Topics Covered), just like Udemy + W3Schools combined.
      </p>

      <div className="flex items-center justify-end gap-4 mt-4">
        <Link to="/my-purchases" data-testid="marketplace-my-purchases-link" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-300 hover:text-white">
          <BookMarked className="w-4 h-4" /> My Purchases
        </Link>
        <Link to="/creator/studio" data-testid="marketplace-become-creator-link" className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-300 hover:text-amber-200">
          <Sparkles className="w-4 h-4" /> Become a Creator
        </Link>
      </div>

      {courses.length === 0 ? (
        <div className="glass-panel rounded-2xl p-10 text-center mt-8" data-testid="courses-marketplace-empty">
          <PlayCircle className="w-10 h-10 text-slate-500 mx-auto mb-3" />
          <p className="text-slate-400 text-sm">No courses published yet. Be the first creator!</p>
          <Link to="/creator/studio" className="inline-block mt-4 bg-primary hover:bg-primary/90 text-white font-semibold text-sm px-5 py-2.5 rounded-lg">
            Create a Course
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
          {courses.map((c, i) => (
            <motion.div key={c.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.06 }}>
              <Link to={`/courses/${c.id}`} data-testid={`course-card-${c.id}`} className="glass-panel rounded-2xl p-5 flex flex-col h-full group hover:-translate-y-1 transition-transform">
                <CourseThumbnail url={c.thumbnail_url} className="mb-4" />
                <h2 className="font-heading text-base font-semibold text-white leading-snug group-hover:text-pink-300 transition-colors">{c.title}</h2>
                <p className="text-xs text-slate-400 mt-1.5 line-clamp-2 flex-1">{c.description}</p>
                {c.review_count > 0 && (
                  <div className="flex items-center gap-1.5 mt-2" data-testid={`course-card-rating-${c.id}`}>
                    <StarRating rating={c.avg_rating} />
                    <span className="text-[11px] text-slate-500">{c.avg_rating} ({c.review_count})</span>
                  </div>
                )}
                <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mt-3">
                  <User className="w-3 h-3" /> {c.creator_name || "Creator"} · {c.video_count} lecture{c.video_count === 1 ? "" : "s"}
                </div>
                <div className="flex items-center justify-between mt-4">
                  <span className="text-sm font-bold text-amber-300">{c.price > 0 ? `₹${c.price.toLocaleString("en-IN")}` : "Free"}</span>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-primary/80 group-hover:bg-primary px-3 py-1.5 rounded-lg transition-colors">
                    View <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
